import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templates, type TeamTemplate, type TemplateRevision, type AgentRole, type WorkflowPhase, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { AgentRoleCard } from '../components/AgentRoleCard';
import { AgentRoleEditor } from '../components/AgentRoleEditor';
import { WorkflowPhaseRow } from '../components/WorkflowPhaseRow';
import { WorkflowPhaseEditor } from '../components/WorkflowPhaseEditor';
import { TemplateRevisionSidebar } from '../components/TemplateRevisionSidebar';

const DEFAULT_AGENT_SETTINGS = {
  model: 'claude-opus-4-6',
  temperature: 0.1,
  maxRetries: 3,
  tools: ['computer', 'bash', 'str_replace_editor'],
  contextWindow: 200000,
};

function newAgentRole(): AgentRole {
  const id = `agent_${Date.now().toString(36)}`;
  return {
    id,
    name: 'New Agent',
    type: 'builder',
    systemPrompt: 'You are a helpful agent.',
    capabilities: [],
    phases: [],
    settings: { ...DEFAULT_AGENT_SETTINGS, tools: [...DEFAULT_AGENT_SETTINGS.tools] },
  };
}

function newWorkflowPhase(name: string): WorkflowPhase {
  return {
    name,
    description: '',
    agents: [],
    autoAdvance: false,
    successCriteria: [],
    color: 'gray',
  };
}

export function TemplateEditor() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isAdmin = user?.isAdmin ?? false;

  const { data, isLoading, error } = useQuery({
    queryKey: ['templates', templateId],
    queryFn: () => templates.get(templateId!),
    enabled: !!templateId,
  });

  const [draft, setDraft] = useState<TeamTemplate | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentRole | null>(null);
  const [editingPhase, setEditingPhase] = useState<WorkflowPhase | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const [activeRevisionTimestamp, setActiveRevisionTimestamp] = useState<string | null>(null);

  const isSystemTemplate = draft?.createdBy === 'system';

  // Reset draft when navigating to a different template
  useEffect(() => {
    setDraft(null);
    setIsDirty(false);
    setActiveRevisionTimestamp(null);
    setEditingAgent(null);
    setEditingPhase(null);
    setConfirmingDelete(false);
    setConfirmingLeave(false);
  }, [templateId]);

  // Sync fetched data into draft (when draft is null or when not dirty)
  useEffect(() => {
    if (data?.data && (!draft || !isDirty)) {
      setDraft(data.data);
    }
  }, [data, draft, isDirty]);

  // Warn on navigation with unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const markDirty = useCallback(() => setIsDirty(true), []);

  const loadRevision = useCallback((revision: TemplateRevision) => {
    if (!revision.snapshot) return;
    setDraft(revision.snapshot);
    setIsDirty(true);
    setActiveRevisionTimestamp(revision.createdAt);
    toast(`Loaded revision v${revision.version} — click Save to apply`, 'info');
  }, [toast]);

  const restoreCurrent = useCallback(() => {
    if (data?.data) {
      setDraft(data.data);
      setIsDirty(false);
      setActiveRevisionTimestamp(null);
    }
  }, [data]);

  // ─── Agent helpers ──────────────────────────────────────────
  const addAgent = () => {
    if (!draft) return;
    const agent = newAgentRole();
    setDraft({ ...draft, agents: [...draft.agents, agent] });
    markDirty();
    setEditingAgent(agent);
  };

  const updateAgent = (updated: AgentRole) => {
    if (!draft) return;
    setDraft({
      ...draft,
      agents: draft.agents.map((a) => (a.id === updated.id ? updated : a)),
    });
    markDirty();
    setEditingAgent(null);
  };

  const removeAgent = (agentId: string) => {
    if (!draft) return;
    setDraft({
      ...draft,
      agents: draft.agents.filter((a) => a.id !== agentId),
      workflow: draft.workflow.map((w) => ({
        ...w,
        agents: w.agents.filter((id) => id !== agentId),
      })),
    });
    markDirty();
  };

  // ─── Workflow helpers ───────────────────────────────────────
  const addWorkflowPhase = () => {
    if (!draft) return;
    const defaultName = 'phase-' + (draft.workflow.length + 1);
    const phase = newWorkflowPhase(defaultName);
    setDraft({ ...draft, workflow: [...draft.workflow, phase] });
    markDirty();
    setEditingPhase(phase);
  };

  const updateWorkflowPhase = (updated: WorkflowPhase) => {
    const originalName = editingPhase?.name;
    if (!originalName) return;
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        workflow: prev.workflow.map((w) => (w.name === originalName ? updated : w)),
      };
    });
    markDirty();
    setEditingPhase(null);
  };

  const removeWorkflowPhase = (phaseName: string) => {
    if (!draft) return;
    setDraft({
      ...draft,
      workflow: draft.workflow.filter((w) => w.name !== phaseName),
    });
    markDirty();
  };

  const movePhase = (index: number, direction: 'up' | 'down') => {
    if (!draft) return;
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= draft.workflow.length) return;
    const workflow = [...draft.workflow];
    [workflow[index], workflow[swapIndex]] = [workflow[swapIndex], workflow[index]];
    setDraft({ ...draft, workflow });
    markDirty();
  };

  // ─── Mutations ──────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('No draft');
      // Always increment from the latest saved version, not the draft
      const currentVersion = data?.data?.version || draft.version;
      const [major, minor, patch] = currentVersion.split('.').map(Number);
      const nextVersion = `${major}.${minor}.${patch + 1}`;
      const updated = { ...draft, version: nextVersion };
      setDraft(updated);
      return templates.update(draft.id, updated);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['templates', templateId] });
      queryClient.invalidateQueries({ queryKey: ['template-revisions', templateId] });
      setIsDirty(false);
      setActiveRevisionTimestamp(null);
      toast('Template saved!', 'success');
    },
    onError: () => toast('Failed to save template', 'error'),
  });

  const forkMutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('No draft');
      return templates.fork(draft.id, { name: draft.name });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast('Template forked!', 'success');
      if (res.data) navigate(`/templates/${res.data.id}`);
    },
    onError: () => toast('Failed to fork template', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('No draft');
      return templates.delete(draft.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast('Template deleted', 'success');
      navigate('/templates');
    },
    onError: () => toast('Failed to delete template', 'error'),
  });

  const publishMutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('No draft');
      return templates.publish(draft.id);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      toast('Template promoted to system template!', 'success');
      if (res.data) navigate(`/templates/${res.data.id}`);
    },
    onError: () => toast('Failed to promote template', 'error'),
  });

  const exportTemplate = async () => {
    if (!draft) return;
    try {
      const res = await templates.exportJson(draft.id);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${draft.name.replace(/\s+/g, '-').toLowerCase()}-v${draft.version}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast('Template exported!', 'success');
    } catch {
      // Fallback: export the draft directly
      const blob = new Blob([JSON.stringify(draft, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${draft.name.replace(/\s+/g, '-').toLowerCase()}-v${draft.version}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast('Template exported (from local draft)', 'info');
    }
  };

  // ─── Loading / Error states ─────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div className="mx-auto max-w-5xl p-8">
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-400">
          Failed to load template.
        </div>
        <button
          onClick={() => navigate('/templates')}
          className="mt-4 text-sm text-primary-400 hover:text-primary-300"
        >
          Back to templates
        </button>
      </div>
    );
  }

  return (
    <div className="flex">
    <div className="mx-auto max-w-5xl flex-1 p-8">
      {/* Header Bar */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {confirmingLeave ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Unsaved changes</span>
              <button
                onClick={() => navigate('/templates')}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-500"
              >
                Leave
              </button>
              <button
                onClick={() => setConfirmingLeave(false)}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
              >
                Stay
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                if (isDirty) { setConfirmingLeave(true); return; }
                navigate('/templates');
              }}
              className="rounded-lg p-2 text-gray-400 hover:bg-gray-800 hover:text-gray-200"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
          )}
          <div>
            {isSystemTemplate ? (
              <h1 className="text-xl font-bold">{draft.name}</h1>
            ) : (
              <input
                value={draft.name}
                onChange={(e) => {
                  setDraft({ ...draft, name: e.target.value });
                  markDirty();
                }}
                className="bg-transparent text-xl font-bold text-gray-100 outline-none focus:underline focus:decoration-primary-500"
              />
            )}
            <div className="mt-1 flex items-center gap-2">
              <span className="rounded bg-gray-800 px-2 py-0.5 text-[10px] font-medium text-gray-400">
                v{draft.version}
              </span>
              {isSystemTemplate && (
                <span className="rounded bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-400">
                  System Template (Read Only)
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isSystemTemplate ? (
            <div className="flex items-center gap-2">
              {isAdmin && (
                confirmingDelete ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { deleteMutation.mutate(); setConfirmingDelete(false); }}
                      className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-500"
                    >
                      Confirm Delete
                    </button>
                    <button
                      onClick={() => setConfirmingDelete(false)}
                      className="rounded-lg px-3 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmingDelete(true)}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10"
                  >
                    Delete
                  </button>
                )
              )}
              <button
                onClick={() => forkMutation.mutate()}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500"
              >
                Fork to Edit
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={() => setShowRevisions(!showRevisions)}
                className={`rounded-lg px-3 py-2 text-sm font-medium ${showRevisions ? 'bg-gray-800 text-gray-200' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'}`}
              >
                History
              </button>
              <button
                onClick={exportTemplate}
                className="rounded-lg px-3 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
              >
                Export
              </button>
              <button
                onClick={() => forkMutation.mutate()}
                className="rounded-lg px-3 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
              >
                Fork
              </button>
              {isAdmin && (
                <button
                  onClick={() => publishMutation.mutate()}
                  disabled={publishMutation.isPending}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-amber-400 hover:bg-amber-500/10 disabled:opacity-40"
                >
                  {publishMutation.isPending ? 'Promoting...' : 'Promote to System'}
                </button>
              )}
              {confirmingDelete ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { deleteMutation.mutate(); setConfirmingDelete(false); }}
                    className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-500"
                  >
                    Confirm Delete
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(false)}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmingDelete(true)}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10"
                >
                  Delete
                </button>
              )}
              <button
                onClick={() => saveMutation.mutate()}
                disabled={!isDirty || saveMutation.isPending}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-40"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Team Section */}
      <section className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400">Team Agents</h2>
          {!isSystemTemplate && (
            <button
              onClick={addAgent}
              className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-primary-400 hover:bg-primary-500/10"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add Agent
            </button>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {draft.agents.map((agent) => (
            <AgentRoleCard
              key={agent.id}
              agent={agent}
              onClick={() => setEditingAgent(agent)}
              onDelete={() => removeAgent(agent.id)}
              readOnly={isSystemTemplate}
            />
          ))}
          {!isSystemTemplate && (
            <button
              onClick={addAgent}
              className="flex min-h-[120px] cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-gray-800 text-gray-600 transition-all hover:border-gray-700 hover:text-gray-500"
            >
              <div className="flex flex-col items-center gap-1">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-xs">Add Agent</span>
              </div>
            </button>
          )}
        </div>

        {draft.agents.length === 0 && isSystemTemplate && (
          <p className="text-sm text-gray-500">No agents configured.</p>
        )}
      </section>

      {/* Workflow Section */}
      <section className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400">Workflow Phases</h2>
          {!isSystemTemplate && (
            <button
              onClick={addWorkflowPhase}
              className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-primary-400 hover:bg-primary-500/10"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add Phase
            </button>
          )}
        </div>

        <div className="space-y-2">
          {draft.workflow.map((phase, i) => (
            <WorkflowPhaseRow
              key={`${phase.name}-${i}`}
              phase={phase}
              agents={draft.agents}
              onClick={() => setEditingPhase(phase)}
              onDelete={() => removeWorkflowPhase(phase.name)}
              onMoveUp={() => movePhase(i, 'up')}
              onMoveDown={() => movePhase(i, 'down')}
              isFirst={i === 0}
              isLast={i === draft.workflow.length - 1}
              readOnly={isSystemTemplate}
            />
          ))}
        </div>

        {draft.workflow.length === 0 && (
          <p className="text-sm text-gray-500">No workflow phases configured.</p>
        )}
      </section>

      {/* Settings Section */}
      {!isSystemTemplate && (
        <section className="mb-8">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-400">Settings</h2>
          <div className="rounded-xl border border-gray-800 bg-surface-850 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Max Concurrent Runs
                </label>
                <input
                  type="number"
                  value={draft.settings.maxConcurrentRuns}
                  onChange={(e) => {
                    setDraft({ ...draft, settings: { ...draft.settings, maxConcurrentRuns: Number(e.target.value) } });
                    markDirty();
                  }}
                  min={1}
                  max={50}
                  className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Retry Count
                </label>
                <input
                  type="number"
                  value={draft.settings.retryCount}
                  onChange={(e) => {
                    setDraft({ ...draft, settings: { ...draft.settings, retryCount: Number(e.target.value) } });
                    markDirty();
                  }}
                  min={0}
                  max={5}
                  className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={draft.settings.autoRetry}
                    onChange={(e) => {
                      setDraft({ ...draft, settings: { ...draft.settings, autoRetry: e.target.checked } });
                      markDirty();
                    }}
                    className="rounded border-gray-600"
                  />
                  <span className="text-sm text-gray-300">Auto-retry on failure</span>
                </label>
              </div>
            </div>

          </div>
        </section>
      )}

      {/* Settings display for system templates */}
      {isSystemTemplate && (
        <section className="mb-8">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-400">Settings</h2>
          <div className="rounded-xl border border-gray-800 bg-surface-850 p-5">
            <div className="grid gap-4 text-sm sm:grid-cols-2">
              <div>
                <span className="text-gray-500">Max Concurrent Runs:</span>{' '}
                <span className="text-gray-200">{draft.settings.maxConcurrentRuns}</span>
              </div>
              <div>
                <span className="text-gray-500">Retry Count:</span>{' '}
                <span className="text-gray-200">{draft.settings.retryCount}</span>
              </div>
              <div>
                <span className="text-gray-500">Auto-retry:</span>{' '}
                <span className="text-gray-200">{draft.settings.autoRetry ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Agent Editor Modal */}
      {editingAgent && (
        <AgentRoleEditor
          agent={editingAgent}
          workflowPhases={draft.workflow}
          onSave={isSystemTemplate ? () => setEditingAgent(null) : updateAgent}
          onClose={() => setEditingAgent(null)}
        />
      )}

      {/* Workflow Phase Editor Modal */}
      {editingPhase && (
        <WorkflowPhaseEditor
          phase={editingPhase}
          agents={draft.agents}
          existingPhaseNames={draft.workflow.map((w) => w.name)}
          onSave={isSystemTemplate ? () => setEditingPhase(null) : updateWorkflowPhase}
          onClose={() => setEditingPhase(null)}
          readOnly={isSystemTemplate}
        />
      )}
    </div>

    {/* Revision History Sidebar */}
    {showRevisions && !isSystemTemplate && (
      <TemplateRevisionSidebar
        templateId={draft.id}
        currentVersion={data?.data?.version || draft.version}
        currentUpdatedAt={data?.data?.updatedAt || draft.updatedAt}
        activeTimestamp={activeRevisionTimestamp}
        onSelectRevision={loadRevision}
        onRestoreCurrent={restoreCurrent}
        onClose={() => setShowRevisions(false)}
      />
    )}
    </div>
  );
}
